import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useStartChat } from '#/api/chats'
import { useCan } from '#/user/me'
import { toChatRunOptions, useChatOptions } from '#/hooks/use-chat-options'
import { PageContainer } from '#/components/page-container'
import { displayTitleClass } from '#/components/page-header'
import { ChatComposer } from '#/components/ask-annwn/chat-composer'
import { StarterQuestions } from '#/components/ask-annwn/starter-questions'

export const Route = createFileRoute('/_authed/accounts/$accountId/ask-annwn/')(
  {
    component: AskAnnwnPage,
  },
)

// The landing surface: no chat selected yet, so there is nothing to load. A chat
// only exists once the first message is sent (the BFF creates it implicitly), so
// this page owns the "new chat" POST and then hands over to the conversation route.
function AskAnnwnPage() {
  const { t } = useTranslation()
  const { accountId } = Route.useParams()
  const navigate = useNavigate()
  const canRead = useCan('annwn:chats:read')
  const canCreate = useCan('annwn:chats:create')
  const canConfigure = useCan('annwn:chats:configure')
  // One instance, shared with the composer's gear: see ChatComposer.options.
  const { options, update } = useChatOptions()
  const start = useStartChat(accountId)

  if (!canRead) {
    return (
      <p className="p-8 text-sm text-muted-foreground">
        {t('askAnnwn.noAccess')}
      </p>
    )
  }

  // Resolves false on failure so the composer keeps the text. A failed create can
  // still have committed the chat (a 503 means only the queueing failed), but the
  // reader stays here with the retryable error rather than being sent into a
  // conversation whose reply already failed.
  const ask = async (content: string) => {
    try {
      const res = await start.mutateAsync({
        content,
        options: toChatRunOptions(options, canConfigure),
      })
      await navigate({
        to: '/accounts/$accountId/ask-annwn/$chatId',
        params: { accountId, chatId: res.chat_uuid },
      })
      return true
    } catch {
      return false
    }
  }

  return (
    <PageContainer width="reading">
      <h1 className={displayTitleClass('xl')}>{t('askAnnwn.title')}</h1>

      {canCreate ? (
        <>
          <ChatComposer
            variant="hero"
            autoFocus
            canConfigure={canConfigure}
            options={options}
            onOptionsChange={update}
            busy={start.isPending}
            error={start.isError ? t('askAnnwn.composer.sendFailed') : null}
            onSubmit={ask}
          />
          <StarterQuestions
            onPick={(question) => {
              void ask(question)
            }}
          />
        </>
      ) : null}
    </PageContainer>
  )
}
